/**
 * DemoScenarioProjections - For demo mode (no API calls)
 * 
 * A self-contained component that calculates scenarios locally
 * without requiring a backend API call.
 * Light theme version matching the site design.
 */

import React, { useState, useEffect } from 'react';
import { Sliders, TrendingUp, TrendingDown, BarChart3, HelpCircle, ChevronDown } from 'lucide-react';

// Info tooltip component
const InfoTooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help inline" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg -left-28 top-6">
      {text}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-gray-200 rotate-45"></div>
    </div>
  </div>
);

interface DemoScenarioProjectionsProps {
  baseResult: {
    final_claim_amount: number;
    indemnity_period_days?: number;
    daily_gross_profit?: number;
  };
}

export default function DemoScenarioProjections({ baseResult }: DemoScenarioProjectionsProps) {
  // Base values from report
  const baseIndemnity = baseResult?.indemnity_period_days || 90;
  const baseClaim = baseResult?.final_claim_amount || 0;
  const baseDailyGP = baseResult?.daily_gross_profit || Math.round(baseClaim / baseIndemnity);
  
  // Scenario adjustments
  const [indemnityDays, setIndemnityDays] = useState(baseIndemnity);
  const [revenueDropPct, setRevenueDropPct] = useState(0);
  const [expenseReductionPct, setExpenseReductionPct] = useState(0);
  
  // Results
  const [scenarioResult, setScenarioResult] = useState<any>(null);
  const [expanded, setExpanded] = useState(true);

  // Calculate scenario locally (no API call)
  useEffect(() => {
    // Simple calculation model for demo purposes
    const indemnityFactor = indemnityDays / baseIndemnity;
    const revenueFactor = 1 - (revenueDropPct / 100);
    const expenseFactor = 1 - (expenseReductionPct / 100);
    
    // Calculate adjusted claim
    const adjustedDailyGP = baseDailyGP * revenueFactor * expenseFactor;
    const projectedClaim = adjustedDailyGP * indemnityDays;
    const claimDelta = projectedClaim - baseClaim;
    const percentChange = baseClaim > 0 ? ((projectedClaim - baseClaim) / baseClaim) * 100 : 0;
    
    setScenarioResult({
      base_claim: baseClaim,
      projected_claim: Math.round(projectedClaim),
      claim_delta: Math.round(claimDelta),
      percent_change: percentChange,
      parameters: {
        indemnity_days: indemnityDays,
        revenue_drop_pct: revenueDropPct,
        expense_reduction_pct: expenseReductionPct
      }
    });
  }, [indemnityDays, revenueDropPct, expenseReductionPct, baseClaim, baseDailyGP, baseIndemnity]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header - Always Visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Sliders className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Loss Projection Scenarios</h3>
              <p className="text-sm text-gray-500">
                Adjust parameters to see how changes affect your claim
              </p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-400 transform transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-6 space-y-6">
          {/* Parameter Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Indemnity Period */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <TrendingUp className="w-4 h-4 mr-2 text-blue-500" />
                Indemnity Period (Days)
                <InfoTooltip text="The number of days the business was interrupted. Longer periods typically result in higher claims." />
              </label>
              <input
                type="range"
                min="30"
                max="365"
                value={indemnityDays}
                onChange={(e) => setIndemnityDays(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>30 days</span>
                <span className="text-blue-600 font-semibold">{indemnityDays} days</span>
                <span>365 days</span>
              </div>
            </div>

            {/* Revenue Drop */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
                Additional Revenue Drop (%)
                <InfoTooltip text="Additional percentage decline in revenue beyond baseline. Use this to model worse-case scenarios." />
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={revenueDropPct}
                onChange={(e) => setRevenueDropPct(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span className="text-red-500 font-semibold">{revenueDropPct}%</span>
                <span>50%</span>
              </div>
            </div>

            {/* Expense Reduction */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <BarChart3 className="w-4 h-4 mr-2 text-green-500" />
                Expense Reduction (%)
                <InfoTooltip text="Cost savings achieved during the closure. Higher savings reduce the net claim amount." />
              </label>
              <input
                type="range"
                min="0"
                max="30"
                value={expenseReductionPct}
                onChange={(e) => setExpenseReductionPct(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span className="text-green-500 font-semibold">{expenseReductionPct}%</span>
                <span>30%</span>
              </div>
            </div>
          </div>

          {/* Results Display */}
          {scenarioResult && (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Base Claim */}
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-1">Base Claim</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(scenarioResult.base_claim)}</p>
                </div>

                {/* Projected Claim */}
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-1">Projected Claim</p>
                  <p className={`text-2xl font-bold ${
                    scenarioResult.projected_claim > scenarioResult.base_claim 
                      ? 'text-green-600' 
                      : scenarioResult.projected_claim < scenarioResult.base_claim 
                        ? 'text-red-600' 
                        : 'text-gray-900'
                  }`}>
                    {formatCurrency(scenarioResult.projected_claim)}
                  </p>
                </div>

                {/* Change */}
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-1">Change</p>
                  <p className={`text-2xl font-bold ${
                    scenarioResult.claim_delta > 0 
                      ? 'text-green-600' 
                      : scenarioResult.claim_delta < 0 
                        ? 'text-red-600' 
                        : 'text-gray-900'
                  }`}>
                    {scenarioResult.claim_delta > 0 ? '+' : ''}
                    {formatCurrency(scenarioResult.claim_delta)}
                    <span className="text-sm ml-1">
                      ({scenarioResult.percent_change > 0 ? '+' : ''}{scenarioResult.percent_change.toFixed(1)}%)
                    </span>
                  </p>
                </div>
              </div>

              {/* Scenario Summary */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  {indemnityDays !== baseIndemnity && (
                    <span className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2 text-xs font-medium">
                      Indemnity: {baseIndemnity} → {indemnityDays} days
                    </span>
                  )}
                  {revenueDropPct > 0 && (
                    <span className="inline-block bg-red-100 text-red-700 px-2 py-1 rounded mr-2 text-xs font-medium">
                      Revenue: -{revenueDropPct}%
                    </span>
                  )}
                  {expenseReductionPct > 0 && (
                    <span className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                      Expenses: -{expenseReductionPct}%
                    </span>
                  )}
                  {indemnityDays === baseIndemnity && revenueDropPct === 0 && expenseReductionPct === 0 && (
                    <span className="text-gray-500">Adjust the sliders above to model different scenarios</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Demo Note */}
          <div className="text-center text-xs text-gray-400 pt-2">
            <p>Demo mode: Calculations are simplified. Sign in for full scenario modeling with saved presets.</p>
          </div>
        </div>
      )}
    </div>
  );
}
